import React, { useEffect, useState } from "react";
import {
  Box,
  CardContent,
  Typography,
  Grid2,
  Button,
  Tab,
  Tabs,
  IconButton,
} from "@mui/material";

// Navigation
import { useNavigate, useSearchParams } from "react-router-dom";

import graphDummyImage from "../../../../assets/images/graphDummyImage.png";

// Styles
import FarmerDetailsStyles from "./FarmerDetailsStyles";

// API Manager
import { getApi } from "../../../../services/apiManager/ApiManager";

// AWS Manager
import { getMediaSignedData } from "../../../../services/awsManager/AwsManager";

// Types & Enums
import {
  IFarmer,
  IFarmerFieldDetails,
} from "../farmerDetailsModal/FarmerDetailsModalTypes";
import { EFieldApprovalStatus } from "../../../../components/shared/commonTypes/commonTypes";

// Dummy image
import farmerDummyImage from "../../../../assets/images/farmerDummyImage.png";
import mapDummyImage from "../../../../assets/images/mapDummyImage.png";

// Hooks
import { useToaster } from "../../../../hooks/useToasterContext/useToasterContext";

// Multi-lingual support
import { t } from "i18next";

//Components & common functions
import Loader from "../../../../components/layout/Loader";
import {
  getFormattedComplianceStatus,
  getFormattedDate,
  getFormattedFloatValue,
} from "../../../../components/shared/commonDataFormatter/CommonDataFormatter";

// Farmer details component
const FarmerDetails = () => {
  // Hooks
  const [searchParams] = useSearchParams();
  const { setShowToaster } = useToaster();
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Get and set farmer data
  const [farmerData, setFarmerData] = useState<IFarmer | null>(null);
  const [farmerImageUrl, setFarmerImageUrl] =
    useState<string>(farmerDummyImage);
  const [approvedFieldList, setApprovedFieldList] = useState<
    IFarmerFieldDetails[]
  >([]);
  const [pendingApprovalList, setPendingApprovalList] = useState<
    IFarmerFieldDetails[]
  >([]);

  // Get farmer ID
  const farmerId = searchParams.get("farmerId");

  // Get farmer details
  const getFarmerDetails = async () => {
    try {
      const apiParams = {
        id: farmerId,
        include_fields: true,
      };
      const response = await getApi("/v1/farmer/details", apiParams);
      if (response.status && response.data) {
        setFarmerData(response.data);
        if (response.data.mediaFileId) {
          const mediaSignedData = await getMediaSignedData(
            response.data.mediaFileId,
          );
          if (mediaSignedData && mediaSignedData.s3SignedUrl) {
            setFarmerImageUrl(mediaSignedData.s3SignedUrl);
          } else {
            setFarmerImageUrl(farmerDummyImage);
          }
        } else {
          setFarmerImageUrl(farmerDummyImage);
        }
      }
    } catch (error) {
      setShowToaster(true, t("commonMessages.internalServerError"), "error");
    }
  };

  useEffect(() => {
    getFarmerDetails();
  }, [farmerId]);

  // Update farmer fields details
  useEffect(() => {
    const updateFieldImages = async () => {
      if (farmerData?.fields && farmerData?.fields.length > 0) {
        setLoading(true);
        const updatedFieldList = (
          await Promise.all(
            farmerData.fields.map(async (item: IFarmerFieldDetails) => {
              if (item.mapFileId) {
                try {
                  const mediaSignedData = await getMediaSignedData(
                    item.mapFileId,
                  );
                  item.fieldMapImageUrl =
                    mediaSignedData?.s3SignedUrl || mapDummyImage;
                } catch {
                  item.fieldMapImageUrl = mapDummyImage;
                }
              } else {
                item.fieldMapImageUrl = mapDummyImage;
              }
              return item;
            }),
          )
        ).sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        );
        // Set updated field list
        farmerData.fields = updatedFieldList;

        // Filter fields
        const approvedFields = updatedFieldList.filter(
          (field) => field.approvalStatus === EFieldApprovalStatus.APPROVED,
        );
        setApprovedFieldList(approvedFields);
        const pendingApprovals = updatedFieldList.filter(
          (field) =>
            field.approvalStatus === EFieldApprovalStatus.PENDING ||
            field.approvalStatus === EFieldApprovalStatus.REJECTED,
        );
        setPendingApprovalList(pendingApprovals);
        setLoading(false);
      }
    };

    updateFieldImages();
  }, [farmerData]);

  // Handle tab change event
  const handleTabChange = (event: React.SyntheticEvent, changedTab: number) => {
    setActiveTab(changedTab);
  };

  // Navigate to Add new field
  const handleAddNewField = () => {
    navigate(`/addField`);
  };

  return (
    <Box p={2}>
      {(!farmerData || !farmerImageUrl) && <Loader />}
      <Box mb={2} display={"flex"} justifyContent={"space-between"}>
        <Typography variant="h6" fontWeight="bold" color="farm.main">
          {t("farmerDetails.farmerDetails")}
        </Typography>
        <Button
          variant="outlined"
          sx={{
            borderColor: "risinblack.main",
            color: "risinblack.main",
          }}
          onClick={() => {
            navigate("/farmers?selectedActiveTab=ENTITY_FARMERS");
          }}
        >
          {/* <Icon className="icon-arrow-left" /> */}
          {t("farmerDetails.back")}
        </Button>
      </Box>
      <Box p={2} className="card-bg-shdw-rdius">
        <Grid2 container gap={3} flexWrap="nowrap">
          <Grid2
            size="auto"
            sx={{ maxWidth: "150px", maxHeight: "155px", overflow: "hidden" }}
          >
            <img
              src={farmerImageUrl}
              alt="Farmer"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Grid2>
          <Grid2 container direction="column" flex={1}>
            <Grid2
              container
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <Typography variant="h6" fontWeight={600} color="risinblack">
                {farmerData?.name} {farmerData?.lastName}
              </Typography>
            </Grid2>

            {/* <Grid2 container spacing={12} alignItems="center" mt={1}>
              {[
                {
                  label: t("farmerDetails.farmerId"),
                  value: farmerData?.seqId,
                },
                {
                  label: t("farmerDetails.agentId"),
                  value: farmerData?.agentSeqId,
                },
                {
                  label: t("farmerDetails.project"),
                  value: farmerData?.projectSeqId,
                },
              ].map((item, index) => (
                <Grid2 key={index} size="auto">
                  <Typography variant="body2" color="risinblack">
                    {item.label}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {item.value}
                  </Typography>
                </Grid2>
              ))}
              <Grid2 size="auto">
                <Typography variant="body2" color="risinblack">
                  {t("farmerDetails.complianceStatus")}
                </Typography>
                <Box mt={0.5}>
                  {getFormattedComplianceStatus({
                    status: farmerData?.complianceStatus as any,
                  })}
                </Box>
              </Grid2>
            </Grid2> */}
            <Grid2
              container
              spacing={2}
              alignItems="center"
              mt={1}
              sx={{ maxHeight: "80px" }}
            >
              {[
                {
                  label: t("farmerDetails.farmerId"),
                  value: farmerData?.seqId,
                  size: 3,
                },
                {
                  label: t("farmerDetails.agentId"),
                  value: farmerData?.agentSeqId,
                  size: 3,
                },
                {
                  label: t("farmerDetails.project"),
                  value: farmerData?.projectSeqId,
                  size: 3,
                },
              ].map((item, index) => (
                <Grid2 key={index} size={item.size}>
                  <Typography variant="body2" color="risinblack">
                    {item.label}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {item.value}
                  </Typography>
                </Grid2>
              ))}

              {/* Compliance status field */}
              <Grid2 size={3}>
                <Typography variant="body2" color="risinblack">
                  {t("farmerDetails.complianceStatus")}
                </Typography>
                <Box mt={0.5}>
                  {getFormattedComplianceStatus({
                    status: farmerData?.complianceStatus as any,
                  })}
                </Box>
              </Grid2>
            </Grid2>

            {/* <Grid2
              container
              spacing={3}
              alignItems="center"
              mt={1}
              sx={{ width: "100%" }}
            >
              {[
                {
                  label: t("farmerDetails.address"),
                  value: farmerData?.address?.trim() || "-",
                  sx: {
                    display: "-webkit-box",
                    width: "250px",
                    minWidth: "250px",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: "1.2",
                    maxHeight: "2.4em",
                    minHeight: "2.4em",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  },
                },
                {
                  label: t("farmerDetails.contactNumber"),
                  value: farmerData?.phoneFull,
                },
                {
                  label: t("farmerDetails.nationalId"),
                  value: farmerData?.nationalId?.trim() ?? "-",
                },
              ].map((item, index) => (
                <Grid2
                  key={index}
                  size={item.label === t("farmerDetails.address") ? 3 : 2}
                >
                  <Typography variant="body2" color="risinblack">
                    {item.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={item.sx || {}}
                  >
                    {item.value}
                  </Typography>
                </Grid2>
              ))}
            </Grid2> */}

            <Grid2
              container
              spacing={2}
              alignItems="center"
              mt={1}
              sx={{ width: "100%" }}
            >
              {[
                {
                  label: t("farmerDetails.address"),
                  value: farmerData?.address?.trim() || "-",
                  size: 3,
                  sx: {
                    display: "-webkit-box",
                    width: "100%",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: "1.2",
                    maxHeight: "2.4em",
                    minHeight: "2.4em",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  },
                },
                {
                  label: t("farmerDetails.contactNumber"),
                  value: farmerData?.phoneFull,
                  size: 3,
                },
                {
                  label: t("farmerDetails.nationalId"),
                  value: farmerData?.nationalId?.trim() ?? "-",
                  size: 3,
                },
              ].map((item, index) => (
                <Grid2 key={index} size={item.size}>
                  <Typography variant="body2" color="risinblack">
                    {item.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={item.sx || {}}
                  >
                    {item.value}
                  </Typography>
                </Grid2>
              ))}
            </Grid2>
          </Grid2>
        </Grid2>
      </Box>
      <Box>
        <Grid2 container spacing={2}>
          <Grid2 size={6}>
            <Grid2 container spacing={2} mt={2}>
              {[
                {
                  value: farmerData?.totalCropCycles,
                  label: t("farmerDetails.totalCropCycles"),
                },
                {
                  value: farmerData?.totalYield,
                  label: t("farmerDetails.totalYield"),
                },
              ].map((stat, index) => (
                <Grid2 size={6} key={index}>
                  <FarmerDetailsStyles.StatCard>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      color="farm.main"
                    >
                      {stat.value}
                    </Typography>
                    <Typography variant="body2">{stat.label}</Typography>
                  </FarmerDetailsStyles.StatCard>
                </Grid2>
              ))}
            </Grid2>
            <Box
              sx={{
                mt: 2,
                backgroundColor: "farm.contrastText",
                borderRadius: 2,
                height: "var(--AgentWebHeight-farmerdetailleftnew)",
                overflowY: "hidden",
              }}
            >
              <Box p={2} className="nograph-outer">
                <Typography sx={{ fontWeight: 500 }}>
                  {t("farmerDetails.yieldProgressPerCycle")}
                </Typography>
                <Box className="nograph-wrapper">
                  <img
                    className="img-responsive"
                    src={graphDummyImage}
                    alt="Yield Progress"
                    style={{ borderRadius: 12 }}
                  />
                </Box>
              </Box>
            </Box>
          </Grid2>
          <Grid2 size={6}>
            <Box
              sx={{
                mt: 2,
                backgroundColor: "farm.contrastText",
                height: "var(--AgentWebHeight-farmerdetailrightnew)",
                overflowY: "auto",
                borderRadius: "8px",
              }}
            >
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "farm.contrastText",
                  zIndex: 1000,
                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                  borderRadius: "8px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1,
                  }}
                >
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      minHeight: 40,
                      borderBottom: "none",
                      textTransform: "none",
                    }}
                    TabIndicatorProps={{ style: { display: "none" } }}
                  >
                    <Tab
                      label={
                        <Button
                          sx={{
                            ...FarmerDetailsStyles.buttonStyles,
                            backgroundColor:
                              activeTab === 0 ? "farm.main" : "transparent",
                            color: activeTab === 0 ? "#fff" : "farm.main",
                            "&:hover": {
                              backgroundColor:
                                activeTab === 0 ? "farm.main" : "transparent",
                              color: activeTab === 0 ? "#fff" : "farm.main",
                            },
                          }}
                        >
                          {t("farmerDetails.approvedFields")}
                          <Box
                            sx={{
                              ...FarmerDetailsStyles.badgeStyle,
                              backgroundColor:
                                activeTab === 0 ? "#fff" : "farm.main",
                              color: activeTab === 0 ? "farm.main" : "#fff",
                            }}
                          >
                            {approvedFieldList.length}
                          </Box>
                        </Button>
                      }
                      sx={{ padding: 0, minWidth: "auto", marginRight: 1 }}
                    />
                    <Tab
                      label={
                        <Button
                          sx={{
                            ...FarmerDetailsStyles.buttonStyles,
                            backgroundColor:
                              activeTab === 1 ? "farm.main" : "transparent",
                            color: activeTab === 1 ? "#fff" : "farm.main",
                            "&:hover": {
                              backgroundColor:
                                activeTab === 1 ? "farm.main" : "transparent",
                              color: activeTab === 1 ? "#fff" : "farm.main",
                            },
                          }}
                        >
                          {t("farmerDetails.pendingApproval")}
                          <Box
                            sx={{
                              ...FarmerDetailsStyles.badgeStyle,
                              backgroundColor:
                                activeTab === 1 ? "#fff" : "farm.main",
                              color: activeTab === 1 ? "farm.main" : "#fff",
                            }}
                          >
                            {pendingApprovalList.length}
                          </Box>
                        </Button>
                      }
                      sx={{ padding: 0, minWidth: "auto" }}
                    />
                  </Tabs>
                  {/* <Button
                    sx={FarmerDetailsStyles.buttonStyles}
                    onClick={handleAddNewField}
                  >
                    <Icon className="icon-addnew" />
                    {t("farmerDetails.addNewField")}
                  </Button> */}
                </Box>
              </Box>
              {(activeTab === 0 && approvedFieldList.length === 0) ||
              (activeTab === 1 && pendingApprovalList.length === 0) ? (
                <Box p={2}>
                  <Box
                    sx={{
                      flexGrow: 1,
                      backgroundColor: "white",
                      padding: 2,
                      borderRadius: 2,
                      marginBottom: 1,
                      boxShadow: 2,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="body1" sx={{ color: "gray" }}>
                      {t("commonMessages.noRecordsFound")}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box p={2}>
                  {isLoading && <Loader />}
                  {(activeTab === 0
                    ? approvedFieldList
                    : pendingApprovalList
                  ).map((field) => (
                    <FarmerDetailsStyles.FieldCard
                      key={field.id}
                      sx={{
                        display: "flex",
                        alignItems: "stretch",
                        height: 120,
                        cursor: "pointer",
                        "&:hover": { boxShadow: 3 },
                      }}
                      onClick={() => {
                        navigate("/FieldDetails?fieldId=" + field.id);
                      }}
                    >
                      <img
                        src={field.fieldMapImageUrl}
                        alt={field.name}
                        width="100"
                        style={{ objectFit: "cover", height: "100%" }}
                      />
                      <CardContent sx={{ flex: 1, pl: 2, py: 0 }}>
                        <Typography fontWeight="bold">{field.name}</Typography>

                        <Grid2 container spacing={2} mt={1}>
                          <Grid2 size={6}>
                            <Typography variant="body2">
                              {t("farmerDetails.createdDate")}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {getFormattedDate(field.date)}
                            </Typography>
                          </Grid2>
                          <Grid2 size={6}>
                            <Typography variant="body2">
                              {t("farmerDetails.fieldId")}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {field.seqId}
                            </Typography>
                          </Grid2>
                        </Grid2>

                        <Grid2 container spacing={2} mt={1}>
                          <Grid2 size={6}>
                            <Typography variant="body2">
                              {t("farmerDetails.fieldArea")}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {getFormattedFloatValue(field.totalArea)}
                            </Typography>
                          </Grid2>
                          <Grid2 size={6}>
                            <Typography variant="body2">
                              {t("farmerDetails.fieldIdentificationNumber")}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {field.fieldIdentificationNumber ?? "-"}
                            </Typography>
                          </Grid2>
                        </Grid2>
                      </CardContent>
                    </FarmerDetailsStyles.FieldCard>
                  ))}
                </Box>
              )}
            </Box>
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
};

export default FarmerDetails;
